import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
  Divider,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  InputAdornment,
  Skeleton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
} from "@mui/material";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import AddCardRoundedIcon from "@mui/icons-material/AddCardRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CreditScoreRoundedIcon from "@mui/icons-material/CreditScoreRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import {
  listPayins,
  initiatePayin,
  checkPayinStatus,
  getPayinGateways,
  listSuperpeCards,
  initiateSuperpeTopup,
  checkSuperpeTopupStatus,
} from "../api";

/* Purpose-based quick-amount suggestions shown under the amount field.
   Education uses round figures; Grocery uses deliberately non-round ones. */
const PURPOSE_AMOUNTS = {
  education: [2200, 5500, 11000, 22400, 23500],
  grocery: [1034, 5077, 10034, 11356, 17666],
  ferry: [2200, 5500, 11000, 22400, 23500],
};

const PURPOSE_LABELS = {
  education: "Education",
  grocery: "Grocery",
  ferry: "Pure Travel",
};

/* BiznessPay exposes ONLY Pure Travel, which runs on the Cashfree account
   (provider "cashfree"). Education/Grocery/Paytm are not offered here. */
const PURPOSE_PROVIDER = {
  ferry: "cashfree",
};

/* Providers that ask for the card last-4 + enforce the per-card daily limit.
   Ferry is intentionally OFF for now — add "ferry" here to switch it on
   (must match CARD_LIMIT_GATEWAYS on the backend). */
const CARD_LIMIT_PROVIDERS = ["cashfree"];

/* Lazily inject the Cashfree JS SDK once and resolve when it's ready. */
let cashfreeSdkPromise = null;
function loadCashfreeSdk() {
  if (typeof window !== "undefined" && window.Cashfree) return Promise.resolve();
  if (cashfreeSdkPromise) return cashfreeSdkPromise;
  cashfreeSdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      cashfreeSdkPromise = null;
      reject(new Error("Failed to load the Cashfree checkout SDK"));
    };
    document.body.appendChild(script);
  });
  return cashfreeSdkPromise;
}

/* Suggest the nearest valid amounts just below what the user typed.
   Offsets 10/20/50/60/100 → e.g. 50000 yields 49990, 49980, 49950,
   49940, 49900. All are multiples of 10 (Cashfree-valid) and avoid
   round-figure patterns. Filtered to > 0 and ≤ max, nearest first. */
function nearbyAmounts(value, max = Infinity) {
  const n = Math.floor(Number(value));
  if (!value || !(n > 0)) return [];
  return [n - 10, n - 20, n - 50, n - 60, n - 100]
    .filter((v) => v > 0 && v <= max)
    .sort((a, b) => b - a);
}

/* Display a card number grouped in 4s (digits only kept in state). */
function formatCardNumber(digits) {
  return String(digits || "")
    .replace(/\D/g, "")
    .slice(0, 19)
    .replace(/(.{4})/g, "$1 ")
    .trim();
}

/* Format expiry as MM/YY as the user types. */
function formatExpiry(v) {
  const d = String(v || "").replace(/\D/g, "").slice(0, 4);
  return d.length <= 2 ? d : `${d.slice(0, 2)}/${d.slice(2)}`;
}

/* Suggest a nearby amount as the user types, tuned to the selected purpose. */
function suggestAmount(purpose, value) {
  const n = Number(value);
  if (!value || !(n > 0)) return null;
  if (purpose === "grocery") {
    const tail = n % 100;
    if (tail === 34 || tail === 77) return null;
    return Math.floor(n / 100) * 100 + (Math.floor(n) % 2 ? 77 : 34);
  }
  const rounded = Math.round(n / 100) * 100;
  return rounded > 0 && rounded !== n ? rounded : null;
}

const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt)
    ? "—"
    : dt.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const formatINR = (v) =>
  v == null ? "—" : `₹${Number(v).toLocaleString("en-IN")}`;

/* card_info / customer_info may arrive as an object, a JSON string, or null */
const parseJson = (v) => {
  if (!v) return null;
  if (typeof v === "object") return v;
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
};

/* Label / value row for the details dialog — stacks on mobile, two columns on sm+ */
function DetailRow({ label, value, mono }) {
  return (
    <Grid container spacing={0.5} sx={{ py: 0.6 }}>
      <Grid item xs={12} sm={5}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={7}>
        {typeof value === "string" || typeof value === "number" ? (
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              fontFamily: mono ? "monospace" : undefined,
              wordBreak: "break-word",
            }}
          >
            {value}
          </Typography>
        ) : (
          value
        )}
      </Grid>
    </Grid>
  );
}

const STATUS_STYLES = {
  success: { label: "Success", color: "success" },
  failed: { label: "Failed", color: "error" },
  expired: { label: "Expired", color: "default" },
  initiated: { label: "Pending", color: "warning" },
};

/* Provider branding for the payment-method picker and table badges.
   Anything with provider "paytm" (or a name containing "paytm") gets the
   Paytm brand accent; everything else gets the generic style. */
const PROVIDER_META = {
  paytm: {
    label: "Paytm",
    tagline: "UPI, cards, net banking & Paytm wallet",
    icon: <BoltRoundedIcon />,
    color: "#002E6E",
  },
  cashfree: {
    label: "Cashfree",
    tagline: "UPI, cards, net banking & wallets",
    icon: <BoltRoundedIcon />,
    color: "#6933FF",
  },
  ferry: {
    label: "Pure Travel",
    tagline: "UPI, cards, net banking & wallets",
    icon: <BoltRoundedIcon />,
    color: "#0e7490",
  },
  generic: {
    label: "SLPE",
    tagline: "UPI, cards & net banking",
    icon: <AccountBalanceRoundedIcon />,
    color: "#6c5ce7",
  },
};

const PHONEPE_META = {
  label: "Card (PhonePe)",
  tagline: "Charge a saved card via PhonePe checkout",
  icon: <CreditScoreRoundedIcon />,
  color: "#5f259f",
};

const isPaytm = (provider, name) =>
  String(provider || "").toLowerCase() === "paytm" ||
  String(name || "").toLowerCase().includes("paytm");

const isCashfree = (provider, name) =>
  String(provider || "").toLowerCase() === "cashfree" ||
  String(name || "").toLowerCase().includes("cashfree");

/* "Ferry & Boat" = the 2nd Cashfree account (provider "ferry"). */
const isFerry = (provider, name) =>
  String(provider || "").toLowerCase() === "ferry" ||
  String(name || "").toLowerCase().includes("ferry");

/* Cashfree-family: same hosted flow, card fields, round-amount + limit. */
const isCashfreeFamily = (provider, name) =>
  isCashfree(provider, name) || isFerry(provider, name);

const providerMeta = (provider, name) =>
  isPaytm(provider, name)
    ? PROVIDER_META.paytm
    : isFerry(provider, name)
      ? PROVIDER_META.ferry
      : isCashfree(provider, name)
        ? PROVIDER_META.cashfree
        : PROVIDER_META.generic;

function GatewayBadge({ gateway }) {
  const meta = isPaytm(gateway)
    ? PROVIDER_META.paytm
    : isFerry(gateway)
      ? PROVIDER_META.ferry
      : isCashfree(gateway)
        ? PROVIDER_META.cashfree
        : {
            label: gateway ? String(gateway).toUpperCase() : "—",
            color: "#90a4ae",
          };
  return (
    <Chip
      size="small"
      label={meta.label}
      sx={{
        fontWeight: 700,
        fontSize: 11,
        color: meta.color,
        bgcolor: alpha(meta.color, 0.12),
      }}
    />
  );
}

/* The single purpose-driven payment card (e.g. "Education • Cashfree").
   The purpose picks the gateway, so this card is always the selected one. */
function PurposeMethodCard({ purpose, gateway }) {
  const meta = providerMeta(gateway.provider, gateway.name);
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        borderRadius: 2.5,
        borderWidth: 2,
        borderColor: meta.color,
        bgcolor: alpha(meta.color, 0.06),
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: meta.color,
          bgcolor: alpha(meta.color, 0.14),
          flexShrink: 0,
        }}
      >
        {meta.icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography fontWeight={700} fontSize={14} noWrap>
          {PURPOSE_LABELS[purpose]} • {meta.label}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {meta.tagline}
        </Typography>
      </Box>
      <CheckCircleRoundedIcon sx={{ color: meta.color, fontSize: 22 }} />
    </Paper>
  );
}

/* Selectable method card driven by a static meta (used for the Card option) */
function MetaMethodCard({ meta, selected, onSelect }) {
  return (
    <Paper
      variant="outlined"
      onClick={onSelect}
      sx={{
        p: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        cursor: "pointer",
        borderRadius: 2.5,
        borderWidth: 2,
        transition: "all .15s ease",
        borderColor: selected ? meta.color : "divider",
        bgcolor: selected ? alpha(meta.color, 0.06) : "background.paper",
        "&:hover": { borderColor: meta.color },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: meta.color,
          bgcolor: alpha(meta.color, 0.14),
          flexShrink: 0,
        }}
      >
        {meta.icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography fontWeight={700} fontSize={14} noWrap>
          {meta.label}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {meta.tagline}
        </Typography>
      </Box>
      {selected && (
        <CheckCircleRoundedIcon sx={{ color: meta.color, fontSize: 22 }} />
      )}
    </Paper>
  );
}

export default function Payins() {
  const [payins, setPayins] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [detailPayin, setDetailPayin] = useState(null);
  const [amount, setAmount] = useState("");
  const [purpose, setPurpose] = useState("ferry");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [pollingId, setPollingId] = useState(null);

  const [payer, setPayer] = useState({ name: "", mobile: "", email: "" });
  /* Cashfree only: the card the payer will use. We keep the full number in the
     field for UX but only ever send/derive the last 4 (pre-check). */
  const [cardNumber, setCardNumber] = useState("");
  const [cardExpiry, setCardExpiry] = useState("");
  const [cardName, setCardName] = useState("");
  const cardLast4 = cardNumber.replace(/\D/g, "").slice(-4);
  /* Error shown INSIDE the Add-Money dialog (so it's visible while it's open). */
  const [dialogError, setDialogError] = useState("");

  const [gateways, setGateways] = useState([]);
  const [gatewaysLoading, setGatewaysLoading] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);

  /* method: "gateway" (Paytm/SLPE) or "card" (SuperPe card top-up) */
  const [method, setMethod] = useState("gateway");
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState("");
  const [cvv, setCvv] = useState("");
  const [superpePollingId, setSuperpePollingId] = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();

  /* Cashfree is an Education-only gateway: it accepts round amounts only.
     When it's the selected method we force the Education purpose, hide the
     Grocery option, and snap the amount to a round figure on submit. */
  const cashfreeSelected =
    method === "gateway" &&
    !!selectedGateway &&
    isCashfreeFamily(selectedGateway.provider, selectedGateway.name);
  /* Whether to collect the card last-4 + apply the per-card limit (cashfree
     only for now; ferry is exempt). */
  const cardLimitSelected =
    cashfreeSelected &&
    CARD_LIMIT_PROVIDERS.includes(
      String(selectedGateway?.provider || "").toLowerCase(),
    );

  /* Cashfree-only round-amount enforcement. "Round figure" = a multiple of
     ₹10 (ends in a zero) — so ₹10, ₹100, ₹2,200 are fine; grocery-style
     tails like ₹10,034 are snapped to the nearest ₹10. */
  const ENFORCE_CASHFREE_ROUNDING = true;
  const roundFigure = (v) => Math.max(10, Math.round(Number(v) / 10) * 10);
  const amountNeedsRounding =
    ENFORCE_CASHFREE_ROUNDING &&
    cashfreeSelected &&
    !!amount &&
    Number(amount) > 0 &&
    Number(amount) % 10 !== 0;

  /* Per-top-up cap for the Cashfree-family gateways (backend enforces the same):
     Education (Cashfree) ₹70,000, Pure Travel ₹50,000. */
  const CASHFREE_MAX =
    selectedGateway && isFerry(selectedGateway.provider, selectedGateway.name)
      ? 50000
      : 70000;
  const amountExceedsCashfreeMax =
    cashfreeSelected && !!amount && Number(amount) > CASHFREE_MAX;

  /* Paytm is capped at ₹20,000 per top-up (front-end guard for now). */
  const paytmSelected =
    method === "gateway" &&
    !!selectedGateway &&
    isPaytm(selectedGateway.provider, selectedGateway.name);
  const PAYTM_MAX = 20000;
  const amountExceedsPaytmMax =
    paytmSelected && !!amount && Number(amount) > PAYTM_MAX;

  const fetchPayins = useCallback(() => {
    setLoading(true);
    listPayins(page)
      .then(({ data }) => {
        setPayins(data.payins || []);
        setTotal(data.pagination?.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => {
    fetchPayins();
  }, [fetchPayins]);

  /* Resume status polling when the gateway redirects back with ?payinId= */
  useEffect(() => {
    const pid = searchParams.get("payinId");
    if (pid) {
      setPollingId(pid);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Poll for payin completion after redirect */
  useEffect(() => {
    if (!pollingId) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await checkPayinStatus(pollingId);
        if (data.status === "success") {
          setFeedback({
            severity: "success",
            msg: `Payment of ${formatINR(data.amount)} successful! ${
              data.netAmount != null
                ? `${formatINR(data.netAmount)} credited to your wallet.`
                : ""
            }`,
          });
          setPollingId(null);
          fetchPayins();
        } else if (data.status === "failed" || data.status === "expired") {
          setFeedback({ severity: "error", msg: `Payment ${data.status}.` });
          setPollingId(null);
          fetchPayins();
        }
      } catch {
        /* keep polling */
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [pollingId, fetchPayins]);

  /* Poll for SuperPe card top-up completion */
  useEffect(() => {
    if (!superpePollingId) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await checkSuperpeTopupStatus(superpePollingId);
        if (data.status === "success") {
          setFeedback({
            severity: "success",
            msg: `Wallet topped up by ${formatINR(data.amount)}`,
          });
          setSuperpePollingId(null);
          fetchPayins();
        } else if (data.status === "failed" || data.status === "expired") {
          setFeedback({
            severity: "error",
            msg: `Card top-up ${data.status}.`,
          });
          setSuperpePollingId(null);
          fetchPayins();
        }
      } catch {
        /* keep polling */
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [superpePollingId, fetchPayins]);

  /* The purpose drives the gateway: resolve the matching gateway from the
     fetched list (Education → Cashfree, Grocery → Paytm) and auto-select it.
     If the resolved gateway isn't available (e.g. an admin disabled it), this
     leaves selectedGateway null so the dialog shows an "unavailable" notice. */
  useEffect(() => {
    const wantProvider = PURPOSE_PROVIDER[purpose];
    const g = gateways.find(
      (x) => String(x.provider || "").toLowerCase() === wantProvider,
    );
    setSelectedGateway(g || null);
  }, [gateways, purpose]);

  const payerMobileOk = /^[6-9]\d{9}$/.test(payer.mobile.trim());
  const payerEmailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payer.email.trim());
  const payerOk = payer.name.trim().length >= 3 && payerMobileOk && payerEmailOk;
  const cvvOk = /^\d{3,4}$/.test(cvv);
  /* Cashfree: collect the card (number + expiry + name). Only the last 4 is
     used for the per-card check; the rest is for the payer's confidence and is
     never sent to our server. */
  const cardNumberDigits = cardNumber.replace(/\D/g, "");
  const cardDetailsOk =
    !cardLimitSelected ||
    (cardNumberDigits.length >= 12 &&
      /^\d{2}\/\d{2}$/.test(cardExpiry) &&
      cardName.trim().length >= 2);

  const canPay =
    !!amount &&
    Number(amount) > 0 &&
    !amountExceedsCashfreeMax &&
    !amountExceedsPaytmMax &&
    cardDetailsOk &&
    (method === "gateway"
      ? !!selectedGateway && payerOk
      : !!selectedCardId && cvvOk);

  const handleOpenDialog = async () => {
    setOpen(true);
    setMethod("gateway");
    setSelectedCardId("");
    setCvv("");
    setCardNumber("");
    setCardExpiry("");
    setCardName("");
    setDialogError("");
    setGatewaysLoading(true);
    try {
      const { data } = await getPayinGateways();
      setGateways(data.gateways || []);
    } catch {
      setGateways([]);
      setSelectedGateway(null);
    }
    setGatewaysLoading(false);

    setCardsLoading(true);
    try {
      const { data } = await listSuperpeCards();
      setCards((data.cards || []).filter((c) => c.usable));
    } catch {
      setCards([]);
    }
    setCardsLoading(false);
  };

  const handleInitiateCard = async () => {
    if (!amount || Number(amount) <= 0 || !selectedCardId || !cvvOk) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      const { data } = await initiateSuperpeTopup(
        Number(amount),
        selectedCardId,
        cvv,
      );
      if (data.paymentUrl) {
        setSuperpePollingId(data.payinId);
        window.open(data.paymentUrl, "_blank");
        setFeedback({
          severity: "info",
          msg: "Complete the card payment in the new tab. The Super Vendor Chrome extension must be installed. This page updates automatically.",
        });
      }
      setOpen(false);
      setAmount("");
      setSelectedCardId("");
      setCvv("");
      fetchPayins();
    } catch (e) {
      setFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Failed to initiate card top-up",
      });
    }
    setSubmitting(false);
  };

  const handleInitiate = async () => {
    if (method === "card") return handleInitiateCard();
    if (!amount || Number(amount) <= 0 || !selectedGateway || !payerOk) return;
    /* Cashfree only accepts round figures — snap to the nearest ₹100
       (enforcement disabled for testing via ENFORCE_CASHFREE_ROUNDING). */
    const payAmount =
      ENFORCE_CASHFREE_ROUNDING && cashfreeSelected
        ? roundFigure(amount)
        : Number(amount);
    setSubmitting(true);
    setFeedback(null);
    setDialogError("");
    try {
      const { data } = await initiatePayin(
        payAmount,
        PURPOSE_LABELS[purpose] || "Wallet Top-up",
        selectedGateway.provider,
        selectedGateway.provider === "slpe"
          ? String(selectedGateway.id)
          : undefined,
        {
          name: payer.name.trim(),
          mobile: payer.mobile.trim(),
          email: payer.email.trim(),
        },
        cardLimitSelected ? cardLast4.trim() : undefined,
      );
      if (data.paymentUrl) {
        /* Paytm / SLPE — hosted page opens in a new tab (unchanged). */
        setPollingId(data.payinId);
        window.open(data.paymentUrl, "_blank");
        setFeedback({
          severity: "info",
          msg: "Payment page opened in a new tab. Complete the payment there — this page will update automatically.",
        });
      } else if (
        isCashfreeFamily(selectedGateway.provider, selectedGateway.name) ||
        data.paymentSessionId
      ) {
        /* Cashfree — render its hosted checkout in an on-page modal via the SDK. */
        await loadCashfreeSdk();
        const cf = window.Cashfree({ mode: data.cashfreeMode || "production" });
        cf.checkout({
          paymentSessionId: data.paymentSessionId,
          redirectTarget: "_modal",
        });
        setPollingId(data.payinId);
        setFeedback({
          severity: "info",
          msg: "Complete the payment in the Cashfree window — this page will update automatically.",
        });
      }
      setOpen(false);
      setAmount("");
      setPayer({ name: "", mobile: "", email: "" });
      fetchPayins();
    } catch (e) {
      /* Show inside the dialog (it stays open on error) so the payer sees it. */
      setDialogError(e.response?.data?.error || "Failed to initiate payment");
    }
    setSubmitting(false);
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Deposits
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add money to your main wallet via Paytm or SLPE
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddCardRoundedIcon />}
          onClick={handleOpenDialog}
          sx={{
            borderRadius: 3,
            px: 3,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: (t) => `0 8px 20px ${alpha(t.palette.primary.main, 0.3)}`,
          }}
        >
          Add Money
        </Button>
      </Stack>

      {feedback && (
        <Alert
          severity={feedback.severity}
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setFeedback(null)}
        >
          {feedback.msg}
        </Alert>
      )}

      {(pollingId || superpePollingId) && (
        <Alert
          severity="info"
          icon={<CircularProgress size={18} />}
          sx={{ mb: 2, borderRadius: 2 }}
        >
          Waiting for payment confirmation… This will update automatically.
        </Alert>
      )}

      <Card sx={{ overflow: "hidden", borderRadius: 3 }}>
        {loading ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : payins.length === 0 ? (
          <Box sx={{ p: 6, textAlign: "center" }}>
            <CreditCardRoundedIcon
              sx={{ fontSize: 52, color: "text.disabled", mb: 1 }}
            />
            <Typography fontWeight={600} mb={0.5}>
              No deposits yet
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Top up your wallet to start transacting
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddCardRoundedIcon />}
              onClick={handleOpenDialog}
              sx={{ borderRadius: 3, textTransform: "none" }}
            >
              Make your first deposit
            </Button>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    fontWeight: 700,
                    fontSize: 12,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    color: "text.secondary",
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell>Amount</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Gateway</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payins.map((p) => {
                const st = STATUS_STYLES[p.status] || {
                  label: p.status,
                  color: "warning",
                };
                return (
                  <TableRow key={p.id} hover>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {formatINR(p.amount)}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>
                      {p.payment_category || "—"}
                    </TableCell>
                    <TableCell>
                      <GatewayBadge gateway={p.gateway} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={st.label}
                        color={st.color}
                        variant={st.color === "default" ? "outlined" : "filled"}
                        sx={{ fontWeight: 600, fontSize: 11 }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>
                      {formatDate(p.created_at)}
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                        alignItems="center"
                      >
                        {/* "Pay Now" (re-open the same order's payment page) was
                            removed: retrying the same order let the gateway
                            complete a payment our side had already expired,
                            causing status mismatches. Start a fresh payin
                            instead. */}
                        <Tooltip title="View details" arrow>
                          <IconButton
                            size="small"
                            onClick={() => setDetailPayin(p)}
                          >
                            <VisibilityRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            </Table>
          </Box>
        )}
        {Math.ceil(total / 20) > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination
              count={Math.ceil(total / 20)}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
            />
          </Box>
        )}
      </Card>

      {/* Deposit dialog */}
      <Dialog
        open={open}
        onClose={() => !submitting && setOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Add Money</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Money is credited to your main wallet after network commissions.
          </Typography>

          {dialogError && (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 2 }}
              onClose={() => setDialogError("")}
            >
              {dialogError}
            </Alert>
          )}

          <ToggleButtonGroup
            value={purpose}
            exclusive
            size="small"
            onChange={(_, v) => v && setPurpose(v)}
            sx={{
              mb: 2,
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                px: 2,
              },
            }}
          >
            <ToggleButton value="ferry">Pure Travel</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{
              min: 1,
              max: cashfreeSelected
                ? CASHFREE_MAX
                : paytmSelected
                  ? PAYTM_MAX
                  : undefined,
            }}
            autoFocus
            error={amountExceedsCashfreeMax || amountExceedsPaytmMax}
            helperText={
              amountExceedsCashfreeMax
                ? `Maximum for this payment is ₹${CASHFREE_MAX.toLocaleString("en-IN")}`
                : amountExceedsPaytmMax
                  ? "Maximum for Paytm is ₹20,000"
                  : ""
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">₹</InputAdornment>
              ),
              sx: { fontWeight: 700, fontSize: 18, borderRadius: 2 },
            }}
          />

          {Number(amount) > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5, mb: 0.5 }}>
              Nearby amounts
            </Typography>
          )}
          <Stack direction="row" spacing={1} mt={Number(amount) > 0 ? 0 : 1.5} flexWrap="wrap" useFlexGap>
            {(Number(amount) > 0
              ? nearbyAmounts(
                  amount,
                  cashfreeSelected
                    ? CASHFREE_MAX
                    : paytmSelected
                      ? PAYTM_MAX
                      : Infinity,
                )
              : (PURPOSE_AMOUNTS[cashfreeSelected ? "education" : purpose] || []).filter(
                  (v) => !paytmSelected || v <= PAYTM_MAX,
                )
            ).map((v) => (
              <Chip
                key={v}
                label={`₹${v.toLocaleString("en-IN")}`}
                onClick={() => setAmount(String(v))}
                variant={Number(amount) === v ? "filled" : "outlined"}
                color={Number(amount) === v ? "primary" : "default"}
                sx={{ fontWeight: 600 }}
              />
            ))}
          </Stack>

          {amountNeedsRounding && (
            <Typography
              variant="caption"
              color="text.secondary"
              onClick={() => setAmount(String(roundFigure(amount)))}
              sx={{ display: "block", mt: 0.75, cursor: "pointer" }}
            >
              Cashfree accepts round amounts — adjusted to{" "}
              <Box
                component="span"
                sx={{ fontWeight: 700, color: "primary.main" }}
              >
                ₹{roundFigure(amount).toLocaleString("en-IN")}
              </Box>
            </Typography>
          )}

          {!cashfreeSelected &&
            (() => {
              const hint = suggestAmount(purpose, amount);
              if (hint == null) return null;
              return (
              <Typography
                variant="caption"
                color="text.secondary"
                onClick={() => setAmount(String(hint))}
                sx={{ display: "block", mt: 0.75, cursor: "pointer" }}
              >
                {purpose === "education"
                  ? "Prefer a rounder amount? Try "
                  : "Suggested amount: "}
                <Box
                  component="span"
                  sx={{ fontWeight: 700, color: "primary.main" }}
                >
                  ₹{hint.toLocaleString("en-IN")}
                </Box>
              </Typography>
              );
            })()}

          {method === "gateway" && (
            <>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                mt={3}
                mb={0.5}
                color="text.secondary"
              >
                Payer details
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Payer full name"
                  value={payer.name}
                  onChange={(e) =>
                    setPayer((p) => ({ ...p, name: e.target.value }))
                  }
                  error={payer.name !== "" && payer.name.trim().length < 3}
                  helperText={
                    payer.name !== "" && payer.name.trim().length < 3
                      ? "Enter the payer's full name"
                      : ""
                  }
                  InputProps={{ sx: { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Payer mobile number"
                  value={payer.mobile}
                  onChange={(e) =>
                    setPayer((p) => ({
                      ...p,
                      mobile: e.target.value.replace(/\D/g, "").slice(0, 10),
                    }))
                  }
                  error={payer.mobile !== "" && !payerMobileOk}
                  helperText={
                    payer.mobile !== "" && !payerMobileOk
                      ? "Enter a valid 10-digit mobile number"
                      : ""
                  }
                  inputProps={{ inputMode: "numeric" }}
                  InputProps={{ sx: { borderRadius: 2 } }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Payer email"
                  type="email"
                  value={payer.email}
                  onChange={(e) =>
                    setPayer((p) => ({ ...p, email: e.target.value }))
                  }
                  error={payer.email !== "" && !payerEmailOk}
                  helperText={
                    payer.email !== "" && !payerEmailOk
                      ? "Enter a valid email address"
                      : ""
                  }
                  InputProps={{ sx: { borderRadius: 2 } }}
                />
                {cardLimitSelected && (
                  <>
                    <TextField
                      fullWidth
                      size="small"
                      label="Card number"
                      value={formatCardNumber(cardNumber)}
                      onChange={(e) =>
                        setCardNumber(
                          e.target.value.replace(/\D/g, "").slice(0, 19),
                        )
                      }
                      placeholder="1234 5678 9012 3456"
                      error={
                        cardNumber !== "" && cardNumberDigits.length < 12
                      }
                      helperText="A card can be used at most twice per day"
                      inputProps={{ inputMode: "numeric" }}
                      InputProps={{ sx: { borderRadius: 2 } }}
                    />
                    <Stack direction="row" spacing={1.5}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Expiry (MM/YY)"
                        value={cardExpiry}
                        onChange={(e) => setCardExpiry(formatExpiry(e.target.value))}
                        placeholder="MM/YY"
                        inputProps={{ inputMode: "numeric" }}
                        InputProps={{ sx: { borderRadius: 2 } }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Name on card"
                        value={cardName}
                        onChange={(e) => setCardName(e.target.value)}
                        InputProps={{ sx: { borderRadius: 2 } }}
                      />
                    </Stack>
                  </>
                )}
              </Stack>
            </>
          )}

          <Typography
            variant="subtitle2"
            fontWeight={700}
            mt={3}
            mb={1}
            color="text.secondary"
          >
            Pay using
          </Typography>

          {gatewaysLoading ? (
            <Stack spacing={1.5}>
              <Skeleton variant="rounded" height={68} />
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              {method === "card" ? (
                <MetaMethodCard meta={PHONEPE_META} selected onSelect={() => {}} />
              ) : selectedGateway ? (
                <PurposeMethodCard purpose={purpose} gateway={selectedGateway} />
              ) : (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  This payment option is currently unavailable
                </Alert>
              )}

              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() =>
                  setMethod((m) => (m === "card" ? "gateway" : "card"))
                }
                sx={{
                  alignSelf: "flex-start",
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                {method === "card"
                  ? `← Back to ${PURPOSE_LABELS[purpose]} payment`
                  : "Pay with a saved card instead"}
              </Link>
            </Stack>
          )}

          {method === "card" && (
            <Box mt={2}>
              {cardsLoading ? (
                <Skeleton variant="rounded" height={56} />
              ) : cards.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  You have no verified cards yet.{" "}
                  <Link component={RouterLink} to="/cards" fontWeight={700}>
                    Add a card
                  </Link>{" "}
                  to top up via PhonePe.
                </Alert>
              ) : (
                <Stack spacing={1.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Card</InputLabel>
                    <Select
                      value={selectedCardId}
                      label="Card"
                      onChange={(e) => setSelectedCardId(e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      {cards.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.network ? `${c.network} ` : ""}•••• {c.last4}
                          {c.nameOnCard ? ` — ${c.nameOnCard}` : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    size="small"
                    label="CVV"
                    value={cvv}
                    onChange={(e) =>
                      setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))
                    }
                    error={cvv !== "" && !cvvOk}
                    helperText={
                      cvv !== "" && !cvvOk ? "Enter the 3-4 digit CVV" : ""
                    }
                    inputProps={{ inputMode: "numeric" }}
                    InputProps={{ sx: { borderRadius: 2 } }}
                  />
                </Stack>
              )}
            </Box>
          )}

          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            mt={2.5}
            sx={{ color: "text.secondary" }}
          >
            <LockRoundedIcon sx={{ fontSize: 15 }} />
            <Typography variant="caption">
              {method === "card"
                ? "The Super Vendor Chrome extension must be installed to complete the payment."
                : "You’ll be redirected to a secure payment page in a new tab."}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={() => setOpen(false)}
            disabled={submitting}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleInitiate}
            disabled={submitting || !canPay}
            sx={{
              borderRadius: 2.5,
              px: 3,
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            {submitting
              ? "Processing…"
              : amount && Number(amount) > 0
                ? `Pay ${formatINR(
                    ENFORCE_CASHFREE_ROUNDING && cashfreeSelected
                      ? roundFigure(amount)
                      : amount,
                  )}`
                : "Proceed to Pay"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payin details dialog */}
      <Dialog
        open={!!detailPayin}
        onClose={() => setDetailPayin(null)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
          Payin Details
        </DialogTitle>
        <DialogContent dividers>
          {detailPayin &&
            (() => {
              const p = detailPayin;
              const card = parseJson(p.card_info);
              const customer = parseJson(p.customer_info);
              const st = STATUS_STYLES[p.status] || {
                label: p.status,
                color: "warning",
              };
              const charges =
                p.total_commission && p.amount
                  ? `${((p.total_commission / p.amount) * 100).toFixed(
                      2,
                    )}% / ${formatINR(p.total_commission)}`
                  : "0% / ₹0";
              return (
                <Box>
                  <DetailRow label="Transaction ID" value={p.id} mono />
                  <DetailRow label="Order ID" value={p.order_no || "—"} mono />
                  {/* For SLPE the gateway mints its own id, so it differs from
                      ours — show it only when it does. */}
                  {p.gateway_order_id &&
                    String(p.gateway_order_id) !== String(p.order_no) && (
                      <DetailRow
                        label="Gateway Order ID"
                        value={p.gateway_order_id}
                        mono
                      />
                    )}
                  {p.gateway_txn_id && (
                    <DetailRow
                      label="Gateway Txn ID"
                      value={p.gateway_txn_id}
                      mono
                    />
                  )}
                  <DetailRow
                    label="Payment Category"
                    value={p.payment_category || "—"}
                  />
                  <DetailRow label="Gateway" value={<GatewayBadge gateway={p.gateway} />} />
                  <DetailRow label="Order Amount" value={formatINR(p.amount)} />
                  <DetailRow label="Detailed charges" value={charges} />
                  <DetailRow
                    label="Credited Amount"
                    value={
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        color="success.main"
                      >
                        {formatINR(p.net_amount)}
                      </Typography>
                    }
                  />

                  {card && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        color="text.secondary"
                        mb={0.5}
                      >
                        Card Details
                      </Typography>
                      <DetailRow
                        label="Issuing Bank"
                        value={card.bank || "—"}
                      />
                      <DetailRow
                        label="Card Type"
                        value={card.cardType || "—"}
                      />
                      <DetailRow label="Sub type" value={card.subType || "—"} />
                      <DetailRow label="Network" value={card.network || "—"} />
                      <DetailRow
                        label="Last 4"
                        value={card.last4 ? `•••• ${card.last4}` : "—"}
                        mono
                      />
                    </>
                  )}

                  {customer && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        color="text.secondary"
                        mb={0.5}
                      >
                        Customer Details
                      </Typography>
                      <DetailRow label="Name" value={customer.name || "—"} />
                      <DetailRow
                        label="Mobile"
                        value={customer.mobile || "—"}
                      />
                      <DetailRow label="Email" value={customer.email || "—"} />
                    </>
                  )}

                  <Divider sx={{ my: 1.5 }} />
                  <DetailRow
                    label="Transaction Date"
                    value={formatDate(p.created_at)}
                  />
                  <DetailRow
                    label="Status"
                    value={
                      <Chip
                        size="small"
                        label={st.label}
                        color={st.color}
                        variant={
                          st.color === "default" ? "outlined" : "filled"
                        }
                        sx={{ fontWeight: 600, fontSize: 11 }}
                      />
                    }
                  />
                </Box>
              );
            })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDetailPayin(null)}
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
